import { Hotel } from "typings/microserviceModels/hotels-flex-module";
import * as React from "react";
import { UitkRating, UitkRatingIcon } from "uitk-react-rating";
import { getHotelLinkRel } from "components/flexComponents/Hotels/components/HotelCard";
import { TrackedLink } from "components/utility/analytics/TrackedLink";

/**
 * CardContent which contains the hotel name, address and overall star rating
 */
export const HotelNameRatingAndAddress = ({
  hotel,
  locale,
  moduleName = "hotels",
  isViewCarousel,
  locationName
}: {
  hotel: Hotel;
  locale: string;
  moduleName?: string;
  isViewCarousel?: boolean;
  locationName?: string;
}) => {
  const {
    hotelStarRating,
    hotelFormattedFullAddress,
    hotelStreetAddress,
    hotelCity,
    callToActionHotelNameUrl,
    distancePoi
  } = hotel;
  const hotelRatingIcon: UitkRatingIcon =
    locale === "en_AU" ? UitkRatingIcon.BRIGHTNESS_1 : UitkRatingIcon.STAR;
  const hotelLink = callToActionHotelNameUrl ? (
    <TrackedLink
      data-testid={`hotelname-link-${hotel.hotelId}`}
      moduleName={moduleName}
      className="hotelNameLink"
      rel={getHotelLinkRel(callToActionHotelNameUrl)}
      rfrr={`hotelName-${hotel.hotelId}`}
      href={callToActionHotelNameUrl}
    >
      <HotelName hotelName={hotel.hotelName} />
    </TrackedLink>
  ) : (
    <HotelName hotelName={hotel.hotelName} />
  );

  /**
   * Return the address of the hotel. First,
   * get the address from hotelFormattedFullAddress which should have the complete information of the address.
   * If it is not present, the address is assigned to hotelStreetAddress and hotelCity.
   * If neither is present, is assigned to hotelCity.
   */
  const getAddress = () => {
    if (hotelFormattedFullAddress) {
      return hotelFormattedFullAddress;
    }
    if (hotelStreetAddress) {
      return `${hotelStreetAddress}, ${hotelCity}`;
    }

    return hotelCity;
  };

  const getDistanceToPOI = () => {
    const distance =
      locale === "en_US" ? `${distancePoi.miles} mi` : `${distancePoi.kilometers} km`;

    return `${distance} from ${locationName}`;
  };

  const hotelAddressOrDistancetoPoi = distancePoi ? getDistanceToPOI() : getAddress();

  return (
    <>
      {!isViewCarousel && <div className="truncate uitk-type-600 uitk-type-bold">{hotelLink}</div>}
      <MemoizedUitkRating hotelRatingIcon={hotelRatingIcon} hotelStarRating={hotelStarRating} />
      <div className={isViewCarousel ? "uitk-type-300 truncate" : "uitk-type-300"}>
        {hotelAddressOrDistancetoPoi}
      </div>
    </>
  );
};

interface MemoizedUitkRatingProps {
  hotelRatingIcon: UitkRatingIcon;
  hotelStarRating: string;
}

const MemoizedUitkRating = React.memo((props: MemoizedUitkRatingProps) => (
  <UitkRating
    overlay={false}
    className="uitk-type-100 uitk-cell all-cell-align-start"
    icon={props.hotelRatingIcon}
    rating={props.hotelStarRating}
  />
));

const HotelName = ({ hotelName }: { hotelName: string }) => (
  <span className="uitk-type-heading-500" data-testid="hotel-name">
    {hotelName}
  </span>
);
